import { Point, Rectangle } from '@printu/geom';
import { generateSimpleArrowPoints, getFancyArrowTriangulation, pointInTriangle, } from 'app/core/graphics/Arrow';
import { Line } from 'app/core/graphics/Line';
import { Path } from 'app/core/graphics/Path';
import { toSegments } from 'app/core/graphics/Segment';
import { getItemAbsoluteBox, getItemAbsolutePoints, getItemAbsoluteVertices, getItemBounds, } from 'app/core/markers/bounds';
export const hitTestArrowMarker = (arrow, point) => {
    const startPoint = new Point(arrow.x, arrow.y);
    const endPoint = new Point(arrow.x + arrow.endX, arrow.y + arrow.endY);
    const arrowWidth = 1.5 * arrow.size;
    switch (arrow.type) {
        case 'fancy': {
            const triangulation = getFancyArrowTriangulation(startPoint, endPoint, arrowWidth);
            return triangulation.some(triangle => pointInTriangle(point, triangle[0], triangle[1], triangle[2]));
        }
        case 'simple':
            {
                const minDistance = arrow.size * 0.875;
                const nLine = new Line(startPoint, endPoint);
                if (nLine.inProximity(point, minDistance, 5)) {
                    return true;
                }
                const [, , left, top, right] = generateSimpleArrowPoints(startPoint, endPoint, arrowWidth);
                return pointInTriangle(point, left, top, right);
            }
            break;
        default:
            return false;
    }
};
export const hitTestLineMarker = (line, point) => {
    const [start, end] = getItemAbsolutePoints(line);
    const minDistance = line.size * 0.875;
    const nLine = new Line(start, end);
    return nLine.inProximity(point, minDistance, 5);
};
export const hitTestPolylineMarker = (polyline, point) => {
    const absolutePoints = [{ x: 0, y: 0 }, ...polyline.points].map(pointAt => ({
        x: pointAt.x + polyline.x,
        y: pointAt.y + polyline.y,
    }));
    return absolutePoints
        .slice(0, -1)
        .map((startPoint, index) => [startPoint, absolutePoints[index + 1]])
        .some(line => {
        const [start, end] = line;
        const minDistance = polyline.size * 0.875;
        const nLine = new Line(start, end);
        return nLine.inProximity(point, minDistance, 5);
    });
};
export const hitTestPathMarker = (path, point, dist = 0.875) => {
    const segments = toSegments(path.segments);
    if (!segments || !segments.length) {
        return false;
    }
    const lPath = new Path(segments);
    const localPoint = new Point(point.x - path.x, point.y - path.y);
    const nearest = lPath.getNearestLocation(localPoint);
    return nearest && nearest.distance <= path.size * dist;
};
export const hitTestHighlightMarker = (highlight, point) => hitTestPathMarker(highlight, point, 1);
export const hitTestSolidRect = (marker, point) => getItemAbsoluteBox(marker).contains(point.x, point.y);
export const hitTestZoomMarker = (marker, point) => {
    const [start, end] = getItemAbsolutePoints(marker);
    // test line
    const minDistance = marker.size * 0.875;
    const nLine = new Line(start, end);
    if (nLine.inProximity(point, minDistance, 5)) {
        return true;
    }
    const { width, height } = marker;
    const localX = point.x - end.x;
    const localY = point.y - end.y;
    // extra check zoom area
    switch (marker.type) {
        case 'rect': {
            const zoomRect = Rectangle.from(-width * 0.5, -height * 0.5, width, height);
            return zoomRect.contains(localX, localY);
        }
        case 'ellipse':
        default: {
            const radius = Math.min(width * 0.5, height * 0.5);
            return Math.sqrt(localX * localX + localY * localY) <= radius;
        }
    }
};
export const hitTestEllipticalBounds = (item, point) => {
    const [tl, , br] = getItemAbsoluteVertices(item);
    const cx = (tl.x + br.x) * 0.5;
    const cy = (tl.y + br.y) * 0.5;
    const xMid = (br.x - tl.x) * 0.5;
    const yMid = (br.y - tl.y) * 0.5;
    return (Math.pow((point.x - cx), 2) / (xMid * xMid) +
        Math.pow((point.y - cy), 2) / (yMid * yMid) <=
        1);
};
export const hitTestRectFrame = (item, point) => {
    const [tl, tr, br, bl] = getItemAbsoluteVertices(item);
    const lines = [
        new Line(tl, tr),
        new Line(tr, br),
        new Line(br, bl),
        new Line(bl, tl),
    ];
    const minDistance = item.size * 0.875;
    return lines.some(line => line.inProximity(point, minDistance, 5));
};
export const hitTestEllipticalFrame = (ellipse, point) => {
    const minDistance = ellipse.size * 0.875;
    const bounds = getItemAbsoluteBox(ellipse);
    const { x: cx, y: cy } = bounds.center;
    const { width: rWidth, height: rHeight } = bounds.size.scaled(0.5);
    const dx = point.x - cx;
    const dy = point.y - cy;
    const isCircle = Math.abs(rHeight - rWidth) < 0.001;
    if (isCircle) {
        const radius = Math.min(rWidth, rHeight);
        const distance = Math.sqrt(dx * dx + dy * dy);
        return Math.abs(radius - distance) <= minDistance;
    }
    /*
        In case of an ellipse we try to find the closest point. In fact we do not calculcate
        closest point but only points of line - ellipse intersection. This is best case is a closes
        point on ellipse and in general is a bit farther from ellipse.

        The solution is for ellipse centered in (0,0) with sami-major/minor w,h and point (px,py)
            x^2 / w^2 + y^2 / h^2 = 1
            y = py / px * x
            ...
            x = sqrt(w * h * px)^2 / ((h * px)^2 + (w * py)^2)
            y = py / px * x

        Due to sqrt there are two solution
            (cx + x, cy + y)
            and
            (cx - x, cy - y)
        So we need to check distance for both
    */
    const x = Math.sqrt(Math.pow((rWidth * rHeight * dx), 2) /
        (Math.pow((rHeight * dx), 2) + Math.pow((rWidth * dy), 2)));
    const y = (dy / dx) * x;
    const solutionDistances = [
        Point.distance({ x: cx + x, y: cy + y }, point),
        Point.distance({ x: cx - x, y: cy - y }, point),
    ];
    return solutionDistances.some(distance => Math.abs(distance) <= minDistance);
};
export const hitTestPin = (marker, point) => {
    const { x, y, width, height } = marker;
    const radius = Math.max(width, height);
    return Point.distance(point, { x, y }) <= radius * 1.1;
};
export const hitTestText = (marker, point) => {
    const { lines, width, height } = marker;
    const bounds = new Rectangle(marker.x, marker.y - lines[0].ascent, width, height);
    return bounds.containsPoint(new Point(point));
};
export const hitTestImage = (item, point) => {
    const bounds = getItemBounds(item);
    return bounds.contains(point.x, point.y);
};
